import { useMemo } from 'react';

import { useTenantAttendanceMetricsContext } from '../contexts/TenantAttendanceMetricsContext';
import type {
  TenantAttendanceMetricsHumanAttendance,
  TenantAttendanceMetricsResponse,
  TenantAttendanceMetricsTelephony,
} from '../types';

interface UseTenantAttendanceMetricsOptions {
  enabled?: boolean;
  initialTelephony?: TenantAttendanceMetricsTelephony | null;
  initialHumanAttendance?: TenantAttendanceMetricsHumanAttendance | null;
}

interface UseTenantAttendanceMetricsReturn {
  isConnected: boolean;
  isSubscribed: boolean;
  isRefreshing: boolean;
  telephony: TenantAttendanceMetricsTelephony | null;
  humanAttendance: TenantAttendanceMetricsHumanAttendance | null;
  lastEvent: TenantAttendanceMetricsResponse | null;
  refresh: (reason?: string) => Promise<void>;
}

/**
 * Read the shared tenant attendance metrics store.
 */
export function useTenantAttendanceMetrics(
  options: UseTenantAttendanceMetricsOptions = {},
): UseTenantAttendanceMetricsReturn {
  const {
    enabled = true,
    initialTelephony = null,
    initialHumanAttendance = null,
  } = options;
  const context = useTenantAttendanceMetricsContext();

  return useMemo(
    () => ({
      isConnected: context.isConnected,
      isSubscribed: enabled ? context.isSubscribed : false,
      isRefreshing: enabled ? context.isRefreshing : false,
      telephony: enabled ? (context.telephony ?? initialTelephony) : initialTelephony,
      humanAttendance: enabled
        ? (context.humanAttendance ?? initialHumanAttendance)
        : initialHumanAttendance,
      lastEvent: enabled ? context.lastPayload : null,
      refresh: context.refresh,
    }),
    [
      context.humanAttendance,
      context.isConnected,
      context.isRefreshing,
      context.isSubscribed,
      context.lastPayload,
      context.refresh,
      context.telephony,
      enabled,
      initialHumanAttendance,
      initialTelephony,
    ],
  );
}

export default useTenantAttendanceMetrics;
